import React, { PropsWithChildren, useCallback } from 'react';
import clsx from 'clsx';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { BlockDescription } from './describeBlock';
import { BlockMenuAction } from './SMLBlockMenu';
import { SMLBlockRowHead } from './SMLBlockRowHead';
import { BlockDrag } from './useBlockDrag';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockRow.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLBlockRowProps {
  widget: SerializedWidget;
  description: BlockDescription;
  index: number;
  count: number;
  isOpen: boolean;
  /** The pointer is on the same block in the preview. */
  isHovered: boolean;
  isNew: boolean;
  canDuplicate: boolean;
  /** Where the dragged row lands next to this one, or null. */
  dropSide: 'above' | 'below' | null;
  drag: BlockDrag;
  onToggle: (widgetUUID: string) => void;
  onAction: (widget: SerializedWidget, action: BlockMenuAction) => void;
  onHover: (widgetUUID: string | null) => void;
}

/**
 * One block of the page (design a1, a2, d0). A click opens its form in place (the children); the
 * list stays. Hover marks the same block in the preview.
 */
const SMLBlockRowComponent: React.FC<PropsWithChildren<SMLBlockRowProps>> = ({
  widget,
  description,
  index,
  count,
  isOpen,
  isHovered,
  isNew,
  canDuplicate,
  dropSide,
  drag,
  onToggle,
  onAction,
  onHover,
  children,
}) => {
  const toggle = useCallback(() => onToggle(widget.uuid), [onToggle, widget.uuid]);
  const action = useCallback((next: BlockMenuAction) => onAction(widget, next), [onAction, widget]);
  const enter = useCallback(() => onHover(widget.uuid), [onHover, widget.uuid]);
  const leave = useCallback(() => onHover(null), [onHover]);
  const { onDragOver, onDrop } = drag;
  const dragOver = useCallback((event: React.DragEvent) => onDragOver(index, event), [index, onDragOver]);
  const isDragged = drag.drag?.widgetUUID === widget.uuid;

  return (
    <div
      className={clsx(
        styles.row,
        isOpen && styles.rowOpen,
        isHovered && styles.rowHovered,
        isDragged && styles.rowDragged,
        dropSide === 'above' && styles.dropAbove,
        dropSide === 'below' && styles.dropBelow
      )}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onDragOver={dragOver}
      onDrop={onDrop}
      data-testid={`block-row-${widget.uuid}`}
      data-open={isOpen}
    >
      <SMLBlockRowHead
        widget={widget}
        description={description}
        isNew={isNew}
        canMoveUp={index > 0}
        canMoveDown={index < count - 1}
        canDuplicate={canDuplicate}
        drag={drag}
        onToggle={toggle}
        onAction={action}
      />
      {isOpen && children !== undefined && <div className={styles.form}>{children}</div>}
    </div>
  );
};

export const SMLBlockRow = TypedMemo(SMLBlockRowComponent);
